/**
 * @zendir/ui - AccessCard Component
 *
 * Enterprise-grade access/link status card between spacecraft and ground station.
 *
 * AstroUXDS Compliance:
 * - Status colors used semantically for link/signal status
 * - Normal (green) = accessible, Off (grey) = no contact
 * - Signal strength uses status gradient (normal → caution → serious → critical)
 * - Zendir accent colors for decorative elements
 * - Tabular numbers for time/data display
 *
 * Features:
 * - Full null-safety with graceful fallbacks
 * - WCAG 2.1 AA accessibility
 * - Link budget display
 * - Upcoming pass windows
 * - Loading and empty states
 */

import React, { memo, useMemo } from "react";
import type { AccessData, AccessWindow } from "../types";
import { useTheme } from "../theme";
import { Icon } from "../core/display/Icon";
import { CardHeader } from "../core/display/CardHeader";
import { useCompactMode } from "../hooks";
import {
  safeNumber,
  formatDuration,
  formatTime,
  formatDataRate,
  formatDecibels,
  formatDegrees,
  classNames,
  getStatusColor,
  safeAccentText,
  type StatusLevel,
} from "../utils";

export interface AccessCardProps {
  /** Current access data */
  access?: AccessData;
  /** Ground station name */
  groundStationName: string;
  /** Spacecraft name */
  spacecraftName?: string;
  /** Next access windows */
  upcomingWindows?: AccessWindow[];
  /** Loading state */
  loading?: boolean;
  /** Custom className */
  className?: string;
  /** Enable compact mode - shows condensed view that expands on hover/click */
  compact?: boolean;
  /** Start expanded when in compact mode (pinned state) */
  defaultExpanded?: boolean;
  /** Callback when pin state changes in compact mode */
  onPinChange?: (isPinned: boolean) => void;
}

/**
 * AccessCard - Displays spacecraft-ground station link status
 *
 * @example
 * ```tsx
 * <AccessCard
 *   access={accessData}
 *   groundStationName="Goldstone"
 *   spacecraftName="ISS"
 *   upcomingWindows={windows}
 * />
 * ```
 */
export const AccessCard = memo(function AccessCard({
  access,
  groundStationName,
  spacecraftName,
  upcomingWindows = [],
  loading = false,
  className = "",
  compact = false,
  defaultExpanded = false,
  onPinChange,
}: AccessCardProps): React.ReactElement {
  const { tokens, theme, prefersReducedMotion } = useTheme();
  const isTransparentTheme =
    theme === "transparent" ||
    theme === "transparent-bold" ||
    theme === "transparent-minimal";
  const isBoldVariant = theme === "transparent-bold";
  const isMinimalVariant = theme === "transparent-minimal";
  const useGlassMode = isBoldVariant || isMinimalVariant;

  // Glass accent colors for bold/minimal themes (matching Container/AstroChart)
  const glassAccentColor = tokens.colors.accent.primary;
  const glassAccentMuted = `${glassAccentColor}66`;

  // Use transparent background with blur for transparent themes (matching Timeline)
  const cardBg = isTransparentTheme
    ? "transparent"
    : tokens.colors.background.surface;
  const cardGlass = isTransparentTheme
    ? {
        backdropFilter: "blur(12px)" as const,
        WebkitBackdropFilter: "blur(12px)" as const,
      }
    : {};

  // Glass mode container style (matching Container/AstroChart for bold/minimal)
  const glassContainerStyle: React.CSSProperties = useGlassMode
    ? {
        backgroundColor: "rgba(10, 15, 25, 0.35)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        border: `1px solid ${glassAccentMuted}`,
        borderTop: isBoldVariant
          ? `2px solid ${glassAccentColor}`
          : `1px solid ${glassAccentColor}`,
      }
    : {};

  // Compact mode state management
  const { expanded, isPinned, handlers, transitionDuration } = useCompactMode({
    compact,
    defaultExpanded,
    onPinChange,
  });

  // Computed status
  const isAccessible = access?.isAccessible ?? false;
  const statusLevel: StatusLevel = isAccessible ? "normal" : "off";
  const statusColor = getStatusColor(statusLevel);

  const signalStatus = useMemo((): StatusLevel => {
    const signal = access?.signalStrength;
    if (signal === undefined || signal === null) return "off";
    if (signal > 70) return "normal";
    if (signal > 40) return "caution";
    if (signal > 20) return "serious";
    return "critical";
  }, [access?.signalStrength]);

  // Loading state
  if (loading) {
    return (
      <div
        className={classNames("zendir-access-card", "loading", className)}
        role="article"
        aria-busy="true"
        aria-label="Loading access data"
        style={{
          backgroundColor: cardBg,
          ...cardGlass,
          ...(tokens.colors.border.cardStyle ?? {}),
          borderRadius: tokens.borderRadius.lg,
          padding: tokens.spacing.md,
          minHeight: 180,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 16,
          }}
        >
          <div
            style={{
              height: 24,
              width: "40%",
              backgroundColor: "rgba(255,255,255,0.1)",
              borderRadius: 4,
            }}
          />
          <div
            style={{
              height: 24,
              width: 60,
              backgroundColor: "rgba(255,255,255,0.1)",
              borderRadius: 4,
            }}
          />
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: 12,
          }}
        >
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              style={{
                height: 40,
                backgroundColor: "rgba(255,255,255,0.06)",
                borderRadius: 4,
              }}
            />
          ))}
        </div>
      </div>
    );
  }

  // Empty state
  if (!access) {
    return (
      <div
        className={classNames("zendir-access-card", "empty", className)}
        role="article"
        aria-label="No access data"
        style={{
          backgroundColor: cardBg,
          ...cardGlass,
          ...(tokens.colors.border.cardStyleDashed ?? {}),
          borderRadius: tokens.borderRadius.lg,
          padding: tokens.spacing.lg,
          textAlign: "center",
          color: tokens.colors.text.tertiary,
        }}
      >
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="currentColor"
          style={{ opacity: 0.5, marginBottom: 8 }}
        >
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
        </svg>
        <p style={{ margin: 0, fontSize: tokens.typography.body[2].fontSize }}>
          {groundStationName}: No access data
        </p>
      </div>
    );
  }

  // Compact view - shows key access info in a single row
  if (compact && !expanded) {
    return (
      <article
        className={classNames("zendir-access-card", "compact", className)}
        {...handlers}
        aria-label={`Access: ${groundStationName} (compact)`}
        style={{
          position: "relative",
          overflow: "hidden",
          ...(useGlassMode
            ? glassContainerStyle
            : {
                backgroundColor: cardBg,
                ...cardGlass,
                ...(tokens.colors.border.cardStyle ?? {}),
              }),
          borderRadius: tokens.borderRadius.lg,
          padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
          fontFamily: tokens.typography.fontFamily.primary,
          color: tokens.colors.text.primary,
          fontSize: tokens.typography.fontSize.sm,
          cursor: "pointer",
          transition: `all ${transitionDuration} ease-out`,
          transform: "scale(1)",
        }}
        onMouseEnter={(e) => {
          handlers.onMouseEnter();
          if (!prefersReducedMotion) {
            (e.currentTarget as HTMLElement).style.transform = "scale(1.01)";
            (e.currentTarget as HTMLElement).style.boxShadow =
              `0 4px 12px ${statusColor}20`;
          }
        }}
        onMouseLeave={(e) => {
          handlers.onMouseLeave();
          (e.currentTarget as HTMLElement).style.transform = "scale(1)";
          (e.currentTarget as HTMLElement).style.boxShadow = "";
        }}
      >
        {/* Glass gradient overlay for bold/minimal themes */}
        {useGlassMode && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(135deg, rgba(255,255,255,0.03) 0%, transparent 50%, rgba(0,0,0,0.1) 100%)",
              pointerEvents: "none",
              zIndex: 0,
            }}
          />
        )}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: tokens.spacing.md,
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* Left side: Status indicator + Name + Accent line */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: tokens.spacing.sm,
              flex: 1,
              minWidth: 0,
            }}
          >
            <div
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: statusColor,
                boxShadow: isAccessible ? `0 0 6px ${statusColor}` : "none",
                flexShrink: 0,
              }}
            />
            <span
              style={{
                fontWeight: tokens.typography.fontWeight.medium,
                color: useGlassMode ? glassAccentColor : undefined,
                textShadow: useGlassMode
                  ? `0 0 16px ${glassAccentMuted}`
                  : undefined,
              }}
            >
              {groundStationName.split(" ")[0]}
            </span>
            {useGlassMode && (
              <div
                style={{
                  flex: 1,
                  height: isBoldVariant ? "2px" : "1px",
                  minWidth: 20,
                  background: `linear-gradient(90deg, ${glassAccentColor} 0%, ${glassAccentMuted} 60%, transparent 100%)`,
                  boxShadow: isBoldVariant
                    ? `0 0 8px ${glassAccentMuted}`
                    : undefined,
                }}
              />
            )}
          </div>

          {/* Right side: Status badge + Signal strength + Pin */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: tokens.spacing.sm,
              flexShrink: 0,
            }}
          >
            <span
              style={{
                color: statusColor,
                fontSize: tokens.typography.fontSize.xs,
                fontWeight: tokens.typography.fontWeight.medium,
              }}
            >
              {isAccessible ? "AOS" : "LOS"}
            </span>
            {access.signalStrength !== undefined && (
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <Icon
                  name="antenna"
                  size={12}
                  color={tokens.colors.text.secondary}
                />
                <span
                  style={{
                    fontVariantNumeric: "tabular-nums",
                    fontFamily: tokens.typography.fontFamily.mono,
                    fontSize: tokens.typography.fontSize.xs,
                  }}
                >
                  {safeNumber(access.signalStrength, 0)}%
                </span>
              </div>
            )}
            {isPinned && (
              <Icon
                name="lock"
                size={12}
                color={tokens.colors.accent.primary}
              />
            )}
          </div>
        </div>
      </article>
    );
  }

  const labelStyle: React.CSSProperties = {
    color: tokens.colors.text.secondary, // WCAG AA compliant - 4.5:1 contrast
    fontSize: tokens.typography.fontSize.xs,
    fontWeight: tokens.typography.fontWeight.medium,
  };

  const valueStyle: React.CSSProperties = {
    color: tokens.colors.text.primary,
    fontWeight: tokens.typography.fontWeight.medium,
    fontFamily: tokens.typography.fontFamily.mono,
    fontVariantNumeric: "tabular-nums",
    marginTop: 2,
  };

  return (
    <article
      className={classNames("zendir-access-card", className)}
      aria-label={`${spacecraftName ?? "Spacecraft"} access to ${groundStationName}`}
      style={{
        position: "relative",
        overflow: "hidden",
        ...(useGlassMode
          ? glassContainerStyle
          : {
              backgroundColor: cardBg,
              ...cardGlass,
              ...(tokens.colors.border.cardStyle ?? {}),
            }),
        borderRadius: tokens.borderRadius.lg,
        padding: tokens.spacing.md,
        fontFamily: tokens.typography.fontFamily.primary,
        color: tokens.colors.text.primary,
        fontSize: tokens.typography.fontSize.sm,
      }}
    >
      {/* Glass gradient overlay for bold/minimal themes */}
      {useGlassMode && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(135deg, rgba(255,255,255,0.03) 0%, transparent 50%, rgba(0,0,0,0.1) 100%)",
            pointerEvents: "none",
            zIndex: 0,
          }}
        />
      )}

      {/* Unified Card Header - handles all theme variants */}
      <CardHeader
        icon="antenna-transmit"
        title={groundStationName}
        subtitle={spacecraftName}
        status={statusLevel}
        badge={isAccessible ? "Link" : "No Link"}
        badgeColor={statusColor}
      />

      {/* Access Metrics */}
      <section
        style={{
          paddingTop: tokens.spacing.md,
          borderTop: `1px solid ${tokens.colors.border.muted}`,
          marginBottom: tokens.spacing.md,
        }}
        aria-label="Access metrics"
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: tokens.spacing.sm,
          }}
        >
          <div>
            <span style={labelStyle}>Elevation</span>
            <div
              style={{
                ...valueStyle,
                color:
                  (access.elevation ?? 0) > 0
                    ? tokens.colors.status.normal
                    : tokens.colors.status.off,
              }}
            >
              {formatDegrees(access.elevation)}
            </div>
          </div>
          <div>
            <span style={labelStyle}>Azimuth</span>
            <div style={valueStyle}>{formatDegrees(access.azimuth)}</div>
          </div>
          <div>
            <span style={labelStyle}>Range</span>
            <div style={valueStyle}>{safeNumber(access.slantRange, 0)} km</div>
          </div>
          <div>
            <span style={labelStyle}>Signal</span>
            <div style={{ ...valueStyle, color: getStatusColor(signalStatus) }}>
              {safeNumber(access.signalStrength, 0, "--")}%
            </div>
          </div>
        </div>
      </section>

      {/* Link Budget */}
      {access.linkBudget && isAccessible && (
        <section
          style={{
            paddingTop: tokens.spacing.md,
            borderTop: `1px solid ${tokens.colors.border.muted}`,
            marginBottom: tokens.spacing.md,
          }}
          aria-label="Link budget"
        >
          <h4
            style={{
              margin: `0 0 ${tokens.spacing.sm} 0`,
              ...labelStyle,
            }}
          >
            Link Budget
          </h4>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: tokens.spacing.sm,
            }}
          >
            <div>
              <span style={labelStyle}>Rx Power</span>
              <div style={valueStyle}>
                {formatDecibels(access.linkBudget.receivedPower)}m
              </div>
            </div>
            <div>
              <span style={labelStyle}>SNR</span>
              <div style={valueStyle}>
                {formatDecibels(access.linkBudget.snr)}
              </div>
            </div>
            <div>
              <span style={labelStyle}>Data Rate</span>
              <div
                style={{
                  ...valueStyle,
                  color: safeAccentText(tokens.colors.accent.primary),
                }}
              >
                {formatDataRate(access.linkBudget.dataRate)}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Upcoming Windows */}
      {upcomingWindows.length > 0 && (
        <section aria-label="Upcoming passes">
          <h4
            style={{
              margin: `0 0 ${tokens.spacing.sm} 0`,
              ...labelStyle,
            }}
          >
            Upcoming Passes
          </h4>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {upcomingWindows.slice(0, 3).map((window, idx) => (
              <div
                key={idx}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: tokens.spacing.sm,
                  backgroundColor: tokens.colors.background.base,
                  borderRadius: tokens.borderRadius.sm,
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: tokens.typography.fontSize.xs,
                      fontWeight: tokens.typography.fontWeight.medium,
                      fontFamily: tokens.typography.fontFamily.mono,
                    }}
                  >
                    {formatTime(String(window.startTime ?? window.aos), false)}
                  </div>
                  <div
                    style={{
                      fontSize: tokens.typography.fontSize.xxs,
                      color: tokens.colors.text.tertiary,
                    }}
                  >
                    {formatDuration(window.duration ?? 0)}
                  </div>
                </div>
                <div
                  style={{
                    fontSize: tokens.typography.fontSize.xs,
                    color: tokens.colors.status.normal,
                    fontFamily: tokens.typography.fontFamily.mono,
                  }}
                >
                  {safeNumber(window.maxElevation, 0)}° max
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </article>
  );
});
